/**
 * Thin HTTP client for the /chat/threads/{tid}/attachments routes.
 *
 * Kept separate from the hook so it can be mocked in tests / reused by
 * thread-hydration code paths.
 */
import { buildApiUrl } from '../../config';
import type { ServerAttachment } from './types';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt') || '';
  return { Authorization: `Bearer ${token}` };
}

function endpoint(tenantId: string, threadId: string, suffix = ''): string {
  return buildApiUrl(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}` +
      `/chat/threads/${encodeURIComponent(threadId)}/attachments${suffix}`,
  );
}

/**
 * Surface a backend error in a user-friendly way.  The router returns
 * either ``{detail: "..."}`` or a FastAPI validation error array.
 */
async function readError(resp: Response): Promise<string> {
  try {
    const body = await resp.json();
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d: { msg?: string }) => d?.msg)
        .filter(Boolean)
        .join('; ');
    }
  } catch {
    /* fall through */
  }
  return `Request failed (${resp.status})`;
}

export async function uploadAttachments(
  tenantId: string,
  threadId: string,
  files: File[],
): Promise<ServerAttachment[]> {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f, f.name));
  const resp = await fetch(endpoint(tenantId, threadId), {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
  });
  if (!resp.ok) throw new Error(await readError(resp));
  const body = (await resp.json()) as { attachments: ServerAttachment[] };
  return body.attachments;
}

export async function listAttachments(
  tenantId: string,
  threadId: string,
  options: { includeDeleted?: boolean } = {},
): Promise<ServerAttachment[]> {
  const suffix = options.includeDeleted ? '?include_deleted=true' : '';
  const resp = await fetch(endpoint(tenantId, threadId) + suffix, {
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error(await readError(resp));
  return (await resp.json()) as ServerAttachment[];
}

export async function getAttachment(
  tenantId: string,
  threadId: string,
  attachmentId: string,
): Promise<ServerAttachment> {
  const resp = await fetch(
    endpoint(tenantId, threadId, `/${encodeURIComponent(attachmentId)}`),
    { headers: authHeaders() },
  );
  if (!resp.ok) throw new Error(await readError(resp));
  return (await resp.json()) as ServerAttachment;
}

export async function deleteAttachment(
  tenantId: string,
  threadId: string,
  attachmentId: string,
): Promise<void> {
  const resp = await fetch(
    endpoint(tenantId, threadId, `/${encodeURIComponent(attachmentId)}`),
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!resp.ok) throw new Error(await readError(resp));
}

export async function confirmParse(
  tenantId: string,
  threadId: string,
  attachmentId: string,
): Promise<ServerAttachment> {
  const resp = await fetch(
    endpoint(tenantId, threadId, `/${encodeURIComponent(attachmentId)}/confirm-parse`),
    { method: 'POST', headers: authHeaders() },
  );
  if (!resp.ok) throw new Error(await readError(resp));
  return (await resp.json()) as ServerAttachment;
}

export function downloadAttachmentUrl(
  tenantId: string,
  threadId: string,
  attachmentId: string,
): string {
  return endpoint(tenantId, threadId, `/${encodeURIComponent(attachmentId)}/download`);
}
